import { JsonObject, JsonProperty } from 'ta-json';
import { RetailerInput } from '@src/app/generated/globalTypes';
import { PriceStock } from '@src/app/model/price-stock';
import { Id } from '@ess/jg-rule-executor';
import { PortalRetailerOptions } from '@src/app/model/portal-retailer-options';
import { PortalSafetyEnvironment } from '@src/app/model/safety-env';
import { PortalColorCodes } from '@src/app/model/portal-color-codes';
import { PortalCurrencyOptions } from '@src/app/model/portal-currency-options';
import { ToJsonInputTranslatable } from '@src/app/model/to-json-input-translatable';
import { GetCurrentRetailer_currentRetailer } from '@src/app/generated/GetCurrentRetailer';
import { GetAllRetailers_allRetailers_edges_node } from '@src/app/generated/GetAllRetailers';
import { AddRetailer_retailer_newRetailer } from '@src/app/generated/AddRetailer';

@JsonObject()
export class Retailer implements ToJsonInputTranslatable {
  @JsonProperty() id: Id<Retailer>;
  @JsonProperty() name: string;
  @JsonProperty() location: string;
  @JsonProperty() currency: string;
  @JsonProperty() email: string;
  @JsonProperty() stock: PriceStock[];
  @JsonProperty() retailerOptions: PortalRetailerOptions;
  @JsonProperty() safetyEnvironment: PortalSafetyEnvironment;
  @JsonProperty() isDeleted: boolean;
  @JsonProperty() rank: number;

  constructor(
    id?: Id<Retailer>,
    name?: string,
    location?: string,
    email?: string,
    stock?: PriceStock[],
    retailerOptions?: PortalRetailerOptions,
    safetyEnv?: PortalSafetyEnvironment,
    isDeleted?: boolean,
    rank?: number
  ) {
    this.id = id;
    this.name = name;
    this.location = location;
    this.email = email;
    this.stock = stock;
    this.retailerOptions = retailerOptions;
    this.safetyEnvironment = safetyEnv ?? new PortalSafetyEnvironment(
      id, [], []
    );
    this.isDeleted = isDeleted;
    this.rank = rank;
  }

  static fromGql(
    gql: GetAllRetailers_allRetailers_edges_node | AddRetailer_retailer_newRetailer | GetCurrentRetailer_currentRetailer
  ): Retailer {
    return new Retailer(
      gql.id,
      gql.name,
      gql.location,
      gql.email,
      gql.priceStocks.edges.map(edge => PriceStock.fromGql(edge.node)),
      PortalRetailerOptions.fromGql(gql.retailerOptions),
      PortalSafetyEnvironment.fromGql(gql.id, gql.safetyEnvironment),
      gql.isDeleted,
      gql.rank
    );
  }

  toGqlInput(): RetailerInput {
    const defaultColorCodes = new PortalColorCodes(...Array(18).fill('#ffffff'));
    return {
      name: this.name ?? 'NAME',
      location: this.location ?? 'LOCATION',
      email: this.email ?? 'email@example.com',
      safetyEnvironment: this.safetyEnvironment.toGqlInput(),
      retailerOptions: (
        this.retailerOptions
        ?? new PortalRetailerOptions(false, new PortalCurrencyOptions('EUR', true, 'nl_NL', false), defaultColorCodes)
      ).toGqlInput(),
      isDeleted: this.isDeleted ?? false,
      rank: this.rank,
    };
  }

  static toJsonInput(retailerInput: RetailerInput): JSON {
    return { retailer: retailerInput } as object as JSON;
  }
}
